import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { QuickFiltersContainer, QuickFilterChip } from '../FilterPanel'
import { Icon } from '../../atoms/Icon'
import { SaveFilterPopover } from '../SaveFilterPopover'
import type { QuickFiltersProps } from './QuickFilters.types'

const ActiveHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 0 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: normal;
  color: ${({ theme }) => theme.colors.text.primary};
`

const CountLabel = styled.span<{ $empty: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: ${({ $empty }) => ($empty ? 0.5 : 1)};
`

const TextButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  line-height: normal;
  color: inherit;
  cursor: pointer;
`

const ResetButton = styled(TextButton)`
  opacity: 0.5;
  margin-left: auto;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      opacity: 1;
    }
  }
`

const SaveButton = styled(TextButton)`
  position: absolute;
  left: 50%;
  bottom: 12px;
  z-index: 1;
  transform: translateX(-50%);
  opacity: 0.5;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      opacity: 1;
    }
  }
`

const ActiveList = styled.div<{ $hasSave: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  min-height: 74px;
  padding: 10px 8px;
  padding-bottom: ${({ $hasSave }) => ($hasSave ? '36px' : '10px')};
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid #eeeeee;
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
`

export const QuickFilters: React.FC<QuickFiltersProps> = ({
  filters,
  onRemoveFilter,
  onReset,
  onSave,
  appliedName,
  className,
  showWhenEmpty = false,
}) => {
  const { t } = useTranslation('ui')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const chipRefs = useRef(new Map<string, HTMLDivElement>())
  const countLabel = t('filters.quickFilters.activeCount', {
    count: filters.length,
  })
  const headerLabel = appliedName || countLabel
  const showSave = Boolean(onSave) && !appliedName

  useEffect(() => {
    if (!selectedId) return

    const deselectOnOutsideClick = (event: PointerEvent) => {
      const selected = chipRefs.current.get(selectedId)
      if (!selected) return
      if (event.target instanceof Node && selected.contains(event.target)) {
        return
      }

      setSelectedId(null)
      if (selected.contains(document.activeElement)) selected.blur()
    }

    document.addEventListener('pointerdown', deselectOnOutsideClick)
    return () => document.removeEventListener('pointerdown', deselectOnOutsideClick)
  }, [selectedId])

  const removeFilter = (id: string) => {
    const index = filters.findIndex((filter) => filter.id === id)
    const neighbor = filters[index + 1] ?? filters[index - 1]
    onRemoveFilter(id)
    if (!neighbor) return
    requestAnimationFrame(() => chipRefs.current.get(neighbor.id)?.focus())
  }

  if (filters.length === 0 && !showWhenEmpty) return null

  return (
    <QuickFiltersContainer className={className}>
      <ActiveHeader>
        <CountLabel $empty={!appliedName && filters.length === 0} title={headerLabel}>
          {headerLabel}
        </CountLabel>
        {onReset && (
          <ResetButton type="button" onClick={onReset}>
            {t('filters.quickFilters.reset')}
          </ResetButton>
        )}
      </ActiveHeader>
      <ActiveList $hasSave={showSave} role="listbox" aria-label={headerLabel}>
        {filters.map((filter) => (
          <QuickFilterChip
            key={filter.id}
            ref={(node) => {
              if (node) chipRefs.current.set(filter.id, node)
              else chipRefs.current.delete(filter.id)
            }}
            role="option"
            tabIndex={0}
            aria-selected={selectedId === filter.id}
            aria-label={filter.label}
            onClick={() => setSelectedId(filter.id)}
            onFocus={() => setSelectedId(filter.id)}
            onKeyDown={(event) => {
              if (event.key !== 'Backspace' && event.key !== 'Delete') return
              event.preventDefault()
              removeFilter(filter.id)
            }}
          >
            <span>{filter.label}</span>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                removeFilter(filter.id)
              }}
              aria-label={t('filters.quickFilters.removeFilter', {
                label: filter.label,
              })}
            >
              <Icon name="x" size="small" />
            </button>
          </QuickFilterChip>
        ))}
      </ActiveList>
      {showSave && onSave && (
        <SaveFilterPopover
          side="bottom"
          align="center"
          filterSummary={filters.map((filter) => ({
            id: filter.id,
            label: filter.label,
          }))}
          onSave={onSave}
          trigger={<SaveButton type="button">{t('filters.quickFilters.save')}</SaveButton>}
        />
      )}
    </QuickFiltersContainer>
  )
}
